import { ApiError, apiFetch } from '@/shared/api/client'
import { shouldUseDelmanMock } from '@/features/delman/api/delmanApiMode'
import type { SubmitDelmanAttendanceRequest } from '@/features/delman/api/submitDelmanAttendance'
import {
  isServerAttendance,
  reconcileDelmanAttendance,
  type DelmanAttendanceAction,
  type ServerAttendance,
} from '@/features/delman/lib/reconcileDelmanAttendance'
import {
  clearDelmanAttendance,
  loadDelmanAttendance,
  saveDelmanAttendance,
  todayDateKey,
  type DelmanDayAttendance,
} from '@/features/delman/storage/delmanAttendanceStorage'
import type { DelmanDeviceBinding } from '@/shared/auth/delmanDeviceStorage'
import { getKitchenSyncQueue, removeKitchenSyncItem } from '@/shared/sync/kitchenSyncQueue'

export const DELMAN_SHIFT = { start: '05:30', end: '14:00' }

const CONFLICT_CODES = new Set([
  'ATTENDANCE_ALREADY_CHECKED_IN',
  'ATTENDANCE_ALREADY_CHECKED_OUT',
  'ATTENDANCE_NOT_CHECKED_IN',
])

type DelmanIdentitySource = Pick<DelmanDeviceBinding, 'delmanId' | 'delmanName' | 'kitchenName'>

export function fetchMyAttendanceToday(): Promise<ServerAttendance> {
  return apiFetch<ServerAttendance>('/attendance/today')
}

/** Snapshot absensi dari konflik 409 absensi, atau null bila bukan konflik absensi. */
export function attendanceConflictSnapshot(err: unknown): ServerAttendance | null {
  if (!(err instanceof ApiError) || err.status !== 409 || !err.code || !CONFLICT_CODES.has(err.code)) {
    return null
  }
  const snapshot = err.body?.attendance
  return isServerAttendance(snapshot) ? snapshot : null
}

/**
 * Item antrean absensi yang ditolak dengan konflik domain bersifat final: retry
 * tidak akan pernah berhasil. `already_*` berarti tujuan sudah tercapai; check-out
 * tanpa check-in di server tidak bisa diproses. Item dihapus dan status lokal
 * dibentuk ulang dari snapshot konflik. Mengembalikan false bila bukan konflik
 * absensi sehingga antrean harus berhenti seperti biasa.
 */
export function resolveQueuedAttendanceConflict(
  itemId: string,
  device: DelmanIdentitySource | null,
  err: unknown,
): boolean {
  if (!(err instanceof ApiError) || err.status !== 409 || !err.code || !CONFLICT_CODES.has(err.code)) {
    return false
  }
  removeKitchenSyncItem(itemId)
  const snapshot = attendanceConflictSnapshot(err)
  if (device && snapshot) applyServerDelmanAttendance(device, snapshot)
  return true
}

function queuedAttendanceItems(delmanId: string) {
  return getKitchenSyncQueue().filter((item) => {
    if (item.type !== 'delman_attendance') return false
    return (item.payload as SubmitDelmanAttendanceRequest | undefined)?.delmanId === delmanId
  })
}

/** Gabungkan status server dengan cache + antrean lokal, lalu simpan hasilnya. */
export function applyServerDelmanAttendance(
  device: DelmanIdentitySource,
  server: ServerAttendance,
): DelmanDayAttendance | null {
  const date = todayDateKey()
  const items = queuedAttendanceItems(device.delmanId)
  const queuedActions = items.map(
    (item) => (item.payload as SubmitDelmanAttendanceRequest).action as DelmanAttendanceAction,
  )

  const { record, resolvedActions } = reconcileDelmanAttendance({
    server,
    local: loadDelmanAttendance(device.delmanId, date),
    queuedActions,
    identity: {
      delmanId: device.delmanId,
      delmanName: device.delmanName,
      kitchenName: device.kitchenName,
      date,
      shiftStart: DELMAN_SHIFT.start,
      shiftEnd: DELMAN_SHIFT.end,
    },
  })

  const resolved = new Set(resolvedActions)
  for (const item of items) {
    if (resolved.has((item.payload as SubmitDelmanAttendanceRequest).action)) {
      removeKitchenSyncItem(item.id)
    }
  }

  if (record) saveDelmanAttendance(record)
  else clearDelmanAttendance(device.delmanId, date)
  return record
}

/**
 * Rehidrasi status absensi dari server. Gagal jaringan/server tidak menyentuh
 * cache maupun antrean lokal; hasilnya `undefined` agar pemanggil tetap memakai
 * data lokal.
 */
export async function syncDelmanAttendanceFromServer(
  device: DelmanIdentitySource,
): Promise<DelmanDayAttendance | null | undefined> {
  if (shouldUseDelmanMock()) return undefined
  if (typeof navigator !== 'undefined' && !navigator.onLine) return undefined
  try {
    const server = await fetchMyAttendanceToday()
    if (!isServerAttendance(server)) return undefined
    return applyServerDelmanAttendance(device, server)
  } catch {
    return undefined
  }
}
